// src/components/messaging/UserSearch.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const UserSearch = ({ onUserSelect }) => {
  const { currentUser, isParent, isProvider, isJobSeeker } = useAuth();
  console.log('UserSearch mounted. currentUser:', currentUser);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Determine which user types to search based on current user type
  const getSearchableUserTypes = () => {
    if (isParent()) {
      return ['providers', 'jobseekers'];
    } else if (isProvider()) {
      return ['parents', 'jobseekers'];
    } else if (isJobSeeker()) {
      return ['parents', 'providers'];
    }
    return [];
  };
  
  // Search users when search term changes
  useEffect(() => {
    const searchUsers = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }
      if (!currentUser || !currentUser.uid) {
        alert('UserSearch: currentUser or currentUser.uid is missing!');
        setSearchResults([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const userTypes = getSearchableUserTypes();
        const searchResultsArray = [];
        // Search each user type collection
        for (const userType of userTypes) {
          const usersRef = collection(db, userType);
          const q = query(
            usersRef,
            where('displayName', '>=', searchTerm),
            where('displayName', '<=', searchTerm + '\uf8ff'),
            limit(5)
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach(doc => {
            // Don't include current user in results
            if (doc.id !== currentUser.uid) {
              searchResultsArray.push({
                id: doc.id,
                ...doc.data(),
                userType
              });
            } else {
              console.log('UserSearch: filtered out current user', doc.id);
            }
          });
        }
        
        // Double-check to ensure current user is not in results array
        const filteredResults = searchResultsArray.filter(user => user.id !== currentUser.uid);
        if (filteredResults.length !== searchResultsArray.length) {
          console.warn('UserSearch: Needed to filter current user again', 
            {before: searchResultsArray.length, after: filteredResults.length});
        }
        
        setSearchResults(filteredResults);
      } catch (err) {
        console.error('Error searching users:', err);
        setError('Failed to search users');
      } finally {
        setLoading(false);
      }
    };
    
    searchUsers();
  }, [searchTerm, currentUser]);
  
  // Get user type label
  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'parents':
        return 'Parent';
      case 'providers':
        return 'Provider';
      case 'jobseekers':
        return 'Job Seeker';
      default:
        return 'User';
    }
  };
  
  // Get user initials for avatar
  const getUserInitials = (displayName) => {
    if (!displayName) return '?';
    
    // Get first letters of first and last name
    const nameParts = displayName.split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    } else {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    }
  };

  // Safe handler for user selection with validation
  const handleUserSelect = (userId) => {
    console.log('UserSearch: selected user', userId, 'currentUser:', currentUser?.uid);
    
    // Multiple safety checks
    if (!userId) {
      console.error('UserSearch: Attempted to select user with no userId');
      return;
    }
    
    if (!currentUser || !currentUser.uid) {
      alert('UserSearch: currentUser or currentUser.uid is missing!');
      return;
    }
    
    if (userId === currentUser.uid) {
      console.error('UserSearch: Attempted to start conversation with self', userId);
      alert('You cannot start a conversation with yourself.');
      return;
    }
    
    // Only call onUserSelect if all validation passes
    onUserSelect(userId);
  };
  
  return (
    <div className="user-search">
      <div className="search-input-container">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search users..."
          className="search-input"
        />
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="search-results">
        {loading ? (
          <div className="loading-results">
            <div className="loading-spinner"></div>
            <p>Searching...</p>
          </div>
        ) : searchTerm.length < 2 ? (
          <div className="search-instructions">
            <p>Type at least 2 characters to search</p>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="empty-results">
            <p>No users found</p>
          </div>
        ) : (
          <ul className="user-list">
            {searchResults.map(user => (
              <li 
                key={user.id} 
                className="user-item"
                onClick={() => handleUserSelect(user.id)}
              >
                <div className="user-avatar">
                  {getUserInitials(user.displayName)}
                </div>
                
                <div className="user-info">
                  <div className="user-name">{user.displayName}</div>
                  <div className="user-type">{getUserTypeLabel(user.userType)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserSearch;